import { type ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import MarkdownTyper, { type IntervalType } from 'react-markdown-typer';
import remarkGfm from 'remark-gfm';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineDownload } from '@/app/components/icons/BaseLineDownload';
import { useCopy } from '@/app/hooks';
import { cn } from '@/app/utils';
import { SKIPPED_FOLDERS } from '../_utils';
import { getFileExtension } from './File/utils';

interface ClawMarkdownProps {
  content: string;
  className?: string;
  normalizeEscapedNewline?: boolean;
  renderAsCodeBlock?: boolean;
  /** 启用打字机动画。默认 false：内容立即渲染，与原 markdown-to-jsx 行为一致。 */
  typing?: boolean;
  /**
   * 打字节奏（ms），仅在 typing 为 true 时生效。
   * - number：恒定间隔
   * - 对象：按剩余字数自适应（剩余越多越快），适合"后端一次塞大段文本"的伪流式场景
   */
  typingInterval?: IntervalType;
  /** 透传 MarkdownTyper 的 onEnd：每段 chunk 打完都会触发一次（包含 streaming 期间），调用方需自行判定终态 */
  onTypingEnd?: () => void;
  /**
   * 点击正文内 `media/` 开头的链接时触发，约定为打开右侧文件预览。
   * 不传时此类链接降级为纯展示的 chip（不可点击），用于分享页 / 文件预览面板内的只读场景。
   */
  onPreviewFile?: (filePath: string) => void;
}

/** 取相对路径的首段目录名（已 trim）。 */
const getRootSegment = (href: string) => href.trim().split('/')[0]?.toLowerCase() ?? '';

/**
 * 正文里指向工作区的链接：根目录是 `media`，或落在 SKIPPED_FOLDERS（`skills`、`sessions`
 * 等系统目录）里，都算工作区内部链接，而不是外链。
 */
const isMediaLink = (href?: string): href is string => {
  if (typeof href !== 'string') return false;
  const root = getRootSegment(href);
  return root === 'media' || SKIPPED_FOLDERS.has(root);
};

/**
 * 工作区内部链接是否可点击查看：
 * - `media/` 下不论文件、文件夹都可点（文件夹进目录，文件开预览）。
 * - SKIPPED_FOLDERS（`skills/` 等系统目录）下只有文件可查看，文件夹静默纯文本展示。
 */
const isClickableMediaLink = (href: string) => getRootSegment(href) === 'media' || getFileExtension(href) !== '';

/**
 * react-markdown 会按 CommonMark 规范对链接 href 做 normalizeUri，把中文等非 ASCII 字符
 * percent-encode（如 `海螺` → `%E6%B5%B7%E8%9E%BA`）。下游要拿真实文件名查工作区产物，
 * 这里解码回来。非法序列时回退原值，避免 decodeURIComponent 抛错。
 */
const decodeMediaPath = (filePath: string): string => {
  try {
    return decodeURIComponent(filePath);
  } catch {
    return filePath;
  }
};

interface MediaFileChipProps {
  filePath: string;
  children: ReactNode;
  onPreviewFile?: (filePath: string) => void;
}

const MediaFileChip = ({ filePath, children, onPreviewFile }: MediaFileChipProps) => {
  // 样式待定：先用一个带边框的 inline 容器占位。
  const className =
    'inline-flex items-center gap-1 rounded-6 border border-system-white16 px-1.5 py-0.5 align-middle text-sm';

  if (!onPreviewFile) {
    return <span className={className}>{children}</span>;
  }

  return (
    <button
      type="button"
      className={cn(className, 'cursor-pointer')}
      onClick={() => onPreviewFile(decodeMediaPath(filePath))}
    >
      {children}
    </button>
  );
};

const DEFAULT_TYPING_INTERVAL: IntervalType = {
  min: 6,
  max: 40,
  curve: 'ease-out',
};

type HastNode = { type?: string; value?: string; children?: HastNode[] };

const extractTextFromHast = (node: HastNode | undefined): string => {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  if (Array.isArray(node.children)) return node.children.map(extractTextFromHast).join('');
  return '';
};

const CodeBlock: NonNullable<Components['pre']> = ({ node, children, className, ...props }) => {
  const { copy } = useCopy();
  const text = useMemo(() => extractTextFromHast(node as HastNode | undefined), [node]);

  const handleDownload = () => {
    if (!text) return;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'code.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="group relative mb-2">
      <pre
        className={cn(
          'relative m-0 max-w-full overflow-x-auto whitespace-pre-wrap rounded-8 bg-system-white08 p-2 [overflow-wrap:anywhere]',
          className,
        )}
        {...props}
      >
        <div className="absolute top-1 right-1 hidden justify-end gap-1 pb-2 group-hover:flex">
          <button
            type="button"
            aria-label="Download code"
            onClick={handleDownload}
            className="inline-flex size-7 items-center justify-center rounded-6 bg-system-white08 text-system-white64 transition-colors hover:bg-system-white16 hover:text-system-white"
          >
            <BaseLineDownload className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Copy code"
            onClick={() => {
              if (text) void copy(text);
            }}
            className="inline-flex size-7 items-center justify-center rounded-6 bg-system-white08 text-system-white64 transition-colors hover:bg-system-white16 hover:text-system-white"
          >
            <BaseLineCopy className="size-4" />
          </button>
        </div>
        {children}
      </pre>
    </div>
  );
};

const createMarkdownComponents = (onPreviewFile?: (filePath: string) => void): Components => ({
  p: ({ node, ...props }) => <p className="m-0 whitespace-pre-line text-sm [overflow-wrap:anywhere]" {...props} />,
  pre: CodeBlock,
  code: ({ node, className, ...props }) => <code className={cn('[overflow-wrap:anywhere]', className)} {...props} />,
  table: ({ node, ...props }) => (
    <table
      className="my-2 w-full border-collapse overflow-hidden rounded-8 border border-system-white12 text-left text-sm"
      {...props}
    />
  ),
  thead: ({ node, ...props }) => <thead className="bg-system-white08" {...props} />,
  tr: ({ node, ...props }) => <tr className="border-system-white12 border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => <th className="px-3 py-2 font-medium text-system-white" {...props} />,
  td: ({ node, ...props }) => (
    <td className="px-3 py-2 align-top text-system-white64 [overflow-wrap:anywhere]" {...props} />
  ),
  hr: ({ node, ...props }) => <hr className="my-2 border-system-white12 border-b" {...props} />,
  a: ({ node, href, children, ...props }) => {
    if (isMediaLink(href)) {
      // SKIPPED_FOLDERS 下的文件夹不可查看，静默降级为纯文本，避免死链。
      if (!isClickableMediaLink(href)) {
        return <>{href}</>;
      }
      return (
        <MediaFileChip filePath={href.trim()} onPreviewFile={onPreviewFile}>
          {children}
        </MediaFileChip>
      );
    }
    return (
      <a target="_blank" rel="noopener noreferrer" href={href} {...props}>
        {children}
      </a>
    );
  },
});

export const ClawMarkdown = ({
  content,
  className,
  normalizeEscapedNewline = false,
  renderAsCodeBlock = false,
  typing = false,
  typingInterval = DEFAULT_TYPING_INTERVAL,
  onTypingEnd,
  onPreviewFile,
}: ClawMarkdownProps) => {
  const markdownContent = useMemo(() => {
    const normalizedContent = normalizeEscapedNewline ? content.replace(/\\n/g, '\n') : content;
    const safeContent = /^---(?:\r?\n|$)/.test(normalizedContent) ? `\n${normalizedContent}` : normalizedContent;
    return renderAsCodeBlock ? `\`\`\`\n${safeContent}\n\`\`\`` : safeContent;
  }, [content, normalizeEscapedNewline, renderAsCodeBlock]);

  const reactMarkdownProps = useMemo(
    () => ({
      remarkPlugins: [remarkGfm],
      components: createMarkdownComponents(onPreviewFile),
    }),
    [onPreviewFile],
  );

  return (
    <div
      className={cn(
        'max-w-full overflow-x-auto text-sm text-system-white80 leading-[22px] [overflow-wrap:anywhere]',
        className,
      )}
    >
      {typing ? (
        <MarkdownTyper
          interval={typingInterval}
          timerType="requestAnimationFrame"
          disableTyping={false}
          experimentalIncrementalRender
          reactMarkdownProps={reactMarkdownProps}
          onEnd={onTypingEnd}
        >
          {markdownContent}
        </MarkdownTyper>
      ) : (
        // 非 typing 场景直接同步渲染。MarkdownTyper 在 mount 第一帧 DOM 还是空壳（pt-10 + avatar = 100px），
        // 之后才异步注入 markdown 真实高度，会让虚拟化拿到的首帧高度全是 100，
        // 后续 RO 把高度回填又触发 totalSize 暴涨/缩水的拉锯，跳转时屏幕看上去剧烈抖动。
        // ReactMarkdown 同步渲染，mount 即终态高度。
        <ReactMarkdown {...reactMarkdownProps}>{markdownContent}</ReactMarkdown>
      )}
    </div>
  );
};
